import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';

import { InvalidError } from '@warp-drive/legacy/adapter/error';
import { RESTAdapter } from '@warp-drive/legacy/adapter/rest';

import config from '../config/environment';

export default class ApplicationAdapter extends RESTAdapter {
  // Services
  @service session;
  @service msal;

  // Defaults
  coalesceFindRequests = true;
  host = config.API.host;
  namespace = config.API.namespace;

  // Functions
  async ajax(url, type, hash) {
    const headers = await this.getHeaders();
    hash = hash ?? {};
    hash.headers = { ...hash.headers, ...headers };

    if (hash && hash.data && !isEmpty(hash.data.ids)) {
      hash['data']['filter'] = {
        id: hash.data.ids,
      };
      hash['data']['limit'] = 1000;
      delete hash['data']['ids'];
    }

    return super.ajax(url, type, hash);
  }

  async getHeaders() {
    const headers = {};
    const isAuthenticated = this.session.isAuthenticated;
    headers['Content-Type'] = 'application/json; charset=utf-8';

    if (!isAuthenticated) {
      return headers;
    }

    const { authenticator, token } = this.session.data.authenticated;

    if (authenticator === 'authenticator:microsoft') {
      try {
        const tokenResponse = await this.msal.getAccessToken();

        headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
      } catch {
        this.session.invalidate();
      }
    } else {
      headers['X-UMG-BACKEND-TOKEN'] = token;
    }

    return headers;
  }

  normalizeErrorResponse(status, headers, payload) {
    if (this._hasLegacyErrorObject(payload)) {
      return [this._normalizeError(status, payload.errors)];
    }

    return super.normalizeErrorResponse(status, headers, payload);
  }

  handleResponse(status, headers, payload) {
    if (this.isInvalid(status, headers, payload)) {
      return new InvalidError(
        this._normalizeInvalidErrorResponse(status, headers, payload),
      );
    }

    return super.handleResponse(...arguments);
  }

  _normalizeInvalidErrorResponse(status, headers, payload) {
    if (this._hasLegacyErrorObject(payload)) {
      return [this._normalizeError(status, payload.errors)];
    }

    return super
      .normalizeErrorResponse(status, headers, payload)
      .map((error) => this._normalizeError(status, error));
  }

  _hasLegacyErrorObject(payload) {
    return (
      payload?.errors &&
      !Array.isArray(payload.errors) &&
      typeof payload.errors === 'object'
    );
  }

  _normalizeError(status, error) {
    let normalizedError = {
      ...error,
      status: `${error.status ?? status}`,
      detail:
        error.detail ?? error.message ?? error.title ?? JSON.stringify(error),
      source: error.source?.pointer ? error.source : { pointer: '/data' },
    };
    delete normalizedError.message;

    return normalizedError;
  }

  pathForType(recordType) {
    if (recordType.startsWith('a11y')) {
      const [path, name] = recordType.split('-');

      return `${path}/${super.pathForType(name)}`;
    }

    return super.pathForType(recordType);
  }
}
